import React from "react";

interface ChatMessageProps {
    content: string;
    className?: string;
    linkClassName?: string;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ content, className, linkClassName }) => {
    // Regular expression to identify URLs and Emails
    // URL: matches http/https protocols or naked domains (e.g. google.com)
    // Email: matches standard email format
    // We use non-capturing groups (?:...) for the inner parts to avoid messing up the split
    const urlRegex = /((?:https?:\/\/)?(?:www\.|[\w-]+\.)+[\w-]{2,}(?:\/[^\s]*)?)/;
    const emailRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/;
    // Combined regex: Email OR URL. Email comes first to avoid partial URL matches on emails.
    const combinedRegex = /((?:[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})|(?:(?:https?:\/\/)?(?:www\.|[\w-]+\.)+[\w-]{2,}(?:\/[^\s]*)?))/g;

    const parts = content.split(combinedRegex);

    // Function to detect text direction
    const getTextDirection = (text: string): "rtl" | "ltr" => {
        // Regex to check for RTL characters (Arabic, Hebrew, Persian, etc.)
        const rtlRegex = /[\u0591-\u07FF\uFB1D-\uFDFD\uFE70-\uFEFC]/;
        return rtlRegex.test(text) ? "rtl" : "ltr";
    };

    const direction = getTextDirection(content);
    const alignmentClass = direction === "rtl" ? "text-right" : "text-left";

    return (
        <div className={`${className} ${alignmentClass}`} dir={direction}>
            {parts.map((part, index) => {
                if (part.match(emailRegex)) {
                    return (
                        <a
                            key={index}
                            href={`mailto:${part}`}
                            className={`hover:underline break-all ${linkClassName || "text-blue-500"}`}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {part}
                        </a>
                    );
                } else if (part.match(urlRegex)) {
                    let href = part;
                    if (!href.startsWith("http://") && !href.startsWith("https://")) {
                        href = `https://${href}`;
                    }
                    return (
                        <a
                            key={index}
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`hover:underline break-all ${linkClassName || "text-blue-500"}`}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {part}
                        </a>
                    );
                }
                return <span key={index}>{part}</span>;
            })}
        </div>
    );
};

export default ChatMessage;
